// Alici mesajlari API istemcisi.
// Etsy'de mesaj/yorum yaniti API'si yok; tek API yolu kargo bildirimine eklenen
// not (note_to_buyer). Digerleri onaylaninca eklenti Etsy sekmesinde gonderir;
// eklenti yoksa kopyala + Etsy'de ac.

import { authHeaders, getBackendUrl } from "./http";
import { callExtension } from "./extension";

export type FlowKind = "order_received" | "order_shipped" | "order_delivered" | "review_reply";

export interface BuyerMessagingConfig {
  since: string;
  minRating: number;
  flows: Record<FlowKind, { enabled: boolean; template: string; multiTemplate?: string }>;
}

export interface BuyerMessage {
  id: string;
  kind: FlowKind;
  refId: string;
  receiptId: string | null;
  buyerUserId: string | null;
  buyerName: string | null;
  listingTitle: string | null;
  rating: number | null;
  reviewText: string | null;
  body: string;
  status: "draft" | "sent" | "failed" | "dismissed";
  channel: "api" | "manual";
  error: string | null;
  eventAt: string | null;
  sentAt: string | null;
  createdAt: string;
}

async function call<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(`${getBackendUrl()}/api/buyer-messages${path}`, { ...init, headers: authHeaders() });
  const json = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(json.error || "İstek başarısız");
  return json as T;
}

export const getBuyerMessagingConfig = () =>
  call<{ data: BuyerMessagingConfig; defaults: Record<FlowKind, string> & { order_received_multi?: string }; replies?: Array<{ key: string; label: string; body: string }> }>("/config");

export const saveBuyerMessagingConfig = (cfg: BuyerMessagingConfig) =>
  call<{ data: BuyerMessagingConfig }>("/config", { method: "PUT", body: JSON.stringify(cfg) }).then((r) => r.data);

export const scanBuyerMessages = () =>
  call<{ data: { created: number } }>("/scan", { method: "POST" }).then((r) => r.data.created);

export function listBuyerMessages(q: { status?: string; receiptId?: string | number; kind?: FlowKind } = {}) {
  const p = new URLSearchParams();
  if (q.status) p.set("status", q.status);
  if (q.receiptId) p.set("receiptId", String(q.receiptId));
  if (q.kind) p.set("kind", q.kind);
  return call<{ data: BuyerMessage[] }>(`/?${p}`).then((r) => r.data);
}

export const updateBuyerMessage = (id: string, patch: { body?: string; status?: BuyerMessage["status"]; error?: string }) =>
  call<{ data: BuyerMessage }>(`/${id}`, { method: "PATCH", body: JSON.stringify(patch) }).then((r) => r.data);

/** Takip numarasini ekler; not verilirse Etsy kargo bildirimiyle aliciya gider. */
export async function shipWithNote(receiptId: string | number, body: {
  tracking_code: string; carrier_name: string; note_to_buyer?: string; buyer_message_id?: string;
}): Promise<void> {
  const res = await fetch(`${getBackendUrl()}/api/etsy/receipts/${receiptId}/tracking`, {
    method: "POST",
    headers: authHeaders(),
    body: JSON.stringify(body),
  });
  if (!res.ok) {
    const j = await res.json().catch(() => ({}));
    throw new Error(j.details ? `${j.error}: ${String(j.details).slice(0, 200)}` : j.error || "Kargo bilgisi eklenemedi");
  }
}

/** API'si olmayan mesajlar icin Etsy'de acilacak sayfa. */
export function etsyComposeUrl(m: BuyerMessage): string {
  if (m.kind === "review_reply") return "https://www.etsy.com/your/shops/me/reviews";
  // Etsy "messages/new?with_id=" adresini gelen kutusuna yonlendiriyor;
  // mesaj siparis ekranindaki "Mesaj gonder" ile acilir.
  return `https://www.etsy.com/your/orders/sold?order_id=${m.receiptId ?? ""}`;
}

export interface ExtensionSendResult { ok: boolean; sent: boolean; error?: string }

/** API'si olmayan mesaji eklentiyle Etsy sekmesinde gonderir. Eklenti yoksa null. */
export function sendViaExtension(m: BuyerMessage, body: string, fillOnly: boolean): Promise<ExtensionSendResult | null> {
  return callExtension<ExtensionSendResult>({
    type: "ETSY_SEND_BUYER_MESSAGE",
    payload: {
      mode: m.kind === "review_reply" ? "review" : "message",
      body,
      buyerUserId: m.buyerUserId ?? undefined,
      receiptId: m.receiptId ?? undefined,
      reviewText: m.reviewText ?? undefined,
      fillOnly,
    },
  });
}
